import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { AUTOPAY_PAYMENT_TYPE_OPTIONS } from '@pesto/features/src/UserProfile/components/AutopaySettings';
import { toAutopayValues } from '@pesto/features/src/UserProfile/utils/toAutopayValues';
import { Typography, useMutationWithToast } from '@pesto/ui';
import { Skeleton } from '@pesto/ui/components/Skeleton';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import {
  useGetLoanAutoPaySettingsQuery,
  useUpdateAutoPaySettingsForUserMutation,
} from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { getDaySuffix } from '../../utils/dateHelper';
import { createFormComponent } from '../utils/create-form-component';

const schema = z.object({
  enabled: z.string(),
  paymentType: z.string().min(1, 'Payment type is required'),
  daysBeforePayment: z
    .string()
    .min(1, 'Days before payment is required')
    .max(28, 'Days before payment must be between 1 and 28'),
});

type FormValues = z.infer<typeof schema>;

export const UpdateAutoPayOptionsComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { data, loading: settingsLoading } = useGetLoanAutoPaySettingsQuery({ variables: { loanId }, skip: !loanId });
  const saved = toAutopayValues(data?.loanAutoPaySettings);

  const { handleMutation } = useMutationWithToast();
  const [mutationAction, { loading }] = useUpdateAutoPaySettingsForUserMutation(getLoanRefetchQuery(loanId));

  if (settingsLoading) {
    return <Skeleton className="h-24 w-full" />;
  }

  if (!saved) {
    return (
      <Typography variant="body" className="text-muted-foreground text-sm">
        This loan has no saved autopay settings. The borrower sets autopay up in the app, which links the bank account
        it pays from.
      </Typography>
    );
  }

  const handleSubmit = (values: FormValues) => {
    void handleMutation({
      mutation: () =>
        mutationAction({
          variables: {
            loanId,
            input: {
              enabled: values.enabled === 'true',
              paymentType: values.paymentType,
              daysBeforePayment: parseInt(values.daysBeforePayment),
              plaidAccountId: saved.plaidAccountId,
            },
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'update-autopay-settings',
      toast: {
        success: 'Autopay settings updated successfully',
        error: 'Failed to update autopay settings',
        loading: 'Updating autopay settings...',
      },
    });
  };

  const config: SmartFormConfig<FormValues> = {
    fields: [
      {
        name: 'enabled',
        label: 'Enable Autopay',
        type: 'select',
        required: true,
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
      {
        name: 'paymentType',
        label: 'Payment Type',
        type: 'select',
        required: true,
        options: AUTOPAY_PAYMENT_TYPE_OPTIONS,
      },
      {
        name: 'daysBeforePayment',
        label: 'Days Before Payment',
        type: 'select',
        required: true,
        options: Array.from({ length: 28 }, (_, i) => ({
          value: String(i + 1),
          label: `${i + 1}${getDaySuffix(i + 1)}`,
        })),
      },
    ],
    schema,
    defaultValues: {
      enabled: String(saved.enabled),
      paymentType: saved.paymentType,
      daysBeforePayment: String(saved.daysBeforePayment),
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
